/**
 * Monitor de Listeners de Firestore
 * Herramienta de debugging para identificar listeners que no se están limpiando
 */

interface ListenerInfo {
  id: string;
  component: string;
  query: string;
  timestamp: number;
  cleaned: boolean;
}

class FirestoreListenerMonitor {
  private listeners: Map<string, ListenerInfo> = new Map();
  private enabled: boolean = import.meta.env.DEV; // Solo en desarrollo
  
  /**
   * Registrar un nuevo listener
   */
  register(component: string, query: string): string {
    if (!this.enabled) return '';
    
    const id = `${component}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.listeners.set(id, {
      id,
      component,
      query,
      timestamp: Date.now(),
      cleaned: false,
    });
    
    this.logStats();
    return id;
  }
  
  /**
   * Marcar listener como limpiado
   */
  unregister(id: string): void {
    if (!this.enabled || !id) return;
    
    const listener = this.listeners.get(id);
    if (listener) {
      listener.cleaned = true;
      this.listeners.delete(id);
      this.logStats();
    }
  }
  
  /**
   * Obtener estadísticas de listeners activos
   */
  getStats(): { total: number; byComponent: Record<string, number>; leaked: ListenerInfo[] } {
    const total = this.listeners.size;
    const byComponent: Record<string, number> = {};
    const leaked: ListenerInfo[] = [];
    
    const now = Date.now();
    const LEAK_THRESHOLD = 30000; // 30 segundos sin cleanup = posible leak
    
    this.listeners.forEach((listener) => {
      // Contar por componente
      byComponent[listener.component] = (byComponent[listener.component] || 0) + 1;
      
      // Detectar posibles leaks
      if (now - listener.timestamp > LEAK_THRESHOLD && !listener.cleaned) {
        leaked.push(listener);
      }
    });
    
    return { total, byComponent, leaked };
  }
  
  /**
   * Loggear estadísticas en consola
   */
  private logStats(): void {
    if (!this.enabled) return;
    
    const stats = this.getStats();

    if (stats.total > 50) {
      console.warn(`⚠️ Firestore: ${stats.total} listeners activos`);
      console.table(stats.byComponent);
    }
    
    if (stats.leaked.length > 0) {
      console.warn(
        `⚠️ Firestore: ${stats.leaked.length} listeners sin cleanup detectados (>30s):`
      );
      console.table(stats.leaked.map((data, index) => ({
        index,
        component: data.component,
        query: data.query.substring(0, 50),
        age: `${Math.floor((Date.now() - data.timestamp) / 1000)}s`,
      })));
    }
  }
  
  /**
   * Limpiar todos los listeners (para debugging)
   */
  clear(): void {
    this.listeners.clear();
    console.log('✅ Monitor de Firestore limpiado');
  }
  
  /**
   * Obtener reporte detallado
   */
  getReport(): string {
    const stats = this.getStats();
    
    let report = `\n=== REPORTE DE LISTENERS DE FIRESTORE ===\n`;
    report += `Total activos: ${stats.total}\n\n`;
    
    report += `Por componente:\n`;
    Object.entries(stats.byComponent)
      .sort((a, b) => b[1] - a[1])
      .forEach(([component, count]) => {
        report += `  ${component}: ${count}\n`;
      });
    
    if (stats.leaked.length > 0) {
      report += `\n⚠️ Posibles leaks (${stats.leaked.length}):\n`;
      stats.leaked.forEach((l) => {
        const age = Math.floor((Date.now() - l.timestamp) / 1000);
        report += `  ${l.component} (${age}s) - ${l.query.substring(0, 60)}\n`;
      });
    }
    
    return report;
  }
}

// Instancia global
export const firestoreMonitor = new FirestoreListenerMonitor();

// Exponer en window para debugging en consola
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).firestoreMonitor = firestoreMonitor;
}

/**
 * Wrapper para onSnapshot con monitoreo automático
 * Úsalo en lugar de onSnapshot directamente
 */
import { onSnapshot as firebaseOnSnapshot, Query, DocumentReference } from 'firebase/firestore';

export function monitoredOnSnapshot(
  reference: Query | DocumentReference,
  onNext: (snapshot: any) => void,
  onError?: (error: Error) => void,
  options?: { component?: string; query?: string }
): () => void {
  const component = options?.component || 'Unknown';
  const query = options?.query || reference.toString().substring(0, 100);
  
  const listenerId = firestoreMonitor.register(component, query);
  
  const unsub = firebaseOnSnapshot(
    reference as any,
    onNext,
    onError || (() => {})
  );
  
  // Retornar función de cleanup que también limpia el monitor
  return () => {
    unsub();
    firestoreMonitor.unregister(listenerId);
  };
}
